import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

const CustomerRegistration = () => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    address: '',
    businessAddress: '',
    gramaNiladhariDivision: '',
    nic: '',
    birthdate: '',
    occupation: '',
    registrationPeriod: '',
    phoneNumber: '',
    spouseFullName: '',
    spouseNic: '',
    spousePhoneNumber: '',
    numberOfDependents: '',
    assetsLands: '',
    assetsVehicles: '',
    assetsOther: '',
    areaId: '',
    // Bank Details
    bankAccountNumber: '',
    bankBranch: '',
    bankName: '',
    // Guarantor 1 Details
    guarantorName: '',
    guarantorAddress: '',
    guarantorBusinessAddress: '',
    guarantorOccupation: '',
    guarantorNic: '',
    guarantorPhoneNumber: '',
    // Guarantor 2 Details
    guarantor2Name: '',
    guarantor2Address: '',
    guarantor2BusinessAddress: '',
    guarantor2Occupation: '',
    guarantor2Nic: '',
    guarantor2PhoneNumber: '',
  });
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [nicErrors, setNicErrors] = useState({
    nic: '',
    spouseNic: '',
    guarantorNic: '',
    guarantor2Nic: '',
  });
  const [birthdateError, setBirthdateError] = useState('');
  const [phoneErrors, setPhoneErrors] = useState({
    phoneNumber: '',
    spousePhoneNumber: '',
    guarantorPhoneNumber: '',
    guarantor2PhoneNumber: '',
  });

  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    try {
      setLoadingAreas(true);
      const response = await api.get('/officer/areas/active');
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
      toast.error('Error loading areas. Please refresh the page.');
    } finally {
      setLoadingAreas(false);
    }
  };

  // NIC validation function
  const validateNIC = (nic) => {
    if (!nic || nic.trim() === '') {
      return ''; // Empty is allowed (for optional fields)
    }
    
    const trimmedNic = nic.trim().toUpperCase();
    
    // Format 1: 10 characters - 9 digits followed by 'V'
    const format1Regex = /^[0-9]{9}V$/;
    
    // Format 2: 12 digits - only numbers
    const format2Regex = /^[0-9]{12}$/;
    
    if (format1Regex.test(trimmedNic)) {
      return ''; // Valid format 1
    }
    
    if (format2Regex.test(trimmedNic)) {
      return ''; // Valid format 2
    }
    
    // Invalid format
    return 'NIC must be either 9 digits followed by V (e.g., 901234567V) or 12 digits (e.g., 199012345678)';
  };

  // Validate Sri Lankan phone number
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === '') {
      return ''; // Empty is allowed (for optional fields)
    }
    
    // Remove all non-digit characters (spaces, dashes, etc.)
    const digits = phone.replace(/[^0-9]/g, '');
    
    // Must be 10 digits starting with 0
    if (digits.length !== 10) {
      return 'Phone number must be 10 digits (e.g., 0712345678 or 0112345678)';
    }
    
    if (!digits.startsWith('0')) {
      return 'Phone number must start with 0 (e.g., 0712345678 for mobile or 0112345678 for landline)';
    }
    
    // Validate mobile number format (07XXXXXXXX)
    const mobileRegex = /^07[0-9]{8}$/;
    if (mobileRegex.test(digits)) {
      // Check if it's a valid mobile prefix (070, 071, 072, 074, 075, 076, 077, 078, 079)
      const prefix = digits.substring(0, 3);
      const validMobilePrefixes = ['070', '071', '072', '074', '075', '076', '077', '078', '079'];
      if (validMobilePrefixes.includes(prefix)) {
        return ''; // Valid mobile number
      }
      return 'Invalid mobile number prefix. Valid prefixes: 070, 071, 072, 074, 075, 076, 077, 078, 079';
    }
    
    // Validate landline format (0XX-XXXXXXX or 0XXXXXXXXX)
    // Common area codes: 011, 021, 023, 024, 025, 026, 027, 031, 032, 033, 034, 035, 036, 037, 038, 041, 045, 047, 051, 052, 053, 054, 055, 057, 063, 065, 066, 067, 081, 082, 091, 094
    const landlineRegex = /^0[0-9]{9}$/;
    if (landlineRegex.test(digits)) {
      // Check if it's a valid landline area code (2-3 digits)
      const areaCode2 = digits.substring(0, 2);
      const areaCode3 = digits.substring(0, 3);
      
      // Common 2-digit area codes
      const valid2DigitAreaCodes = ['01', '02', '03', '04', '05', '06', '08', '09'];
      // Common 3-digit area codes
      const valid3DigitAreaCodes = ['011', '021', '023', '024', '025', '026', '027', '031', '032', '033', '034', '035', '036', '037', '038', '041', '045', '047', '051', '052', '053', '054', '055', '057', '063', '065', '066', '067', '081', '082', '091', '094'];
      
      if (valid3DigitAreaCodes.includes(areaCode3) || valid2DigitAreaCodes.includes(areaCode2)) {
        return ''; // Valid landline number
      }
      // Still accept if it starts with 0 and has 10 digits (might be a valid area code we don't have listed)
      return ''; // Accept as valid (some area codes might not be in our list)
    }
    
    return 'Invalid phone number format. Must be 10 digits starting with 0 (e.g., 0712345678 for mobile or 0112345678 for landline)';
  };

  // Validate birthdate against NIC
  const validateBirthdateWithNIC = (nic, birthdate) => {
    if (!nic || !birthdate) {
      return ''; // If either is empty, no validation needed
    }
    
    const trimmedNic = nic.trim().toUpperCase();
    const birthYear = new Date(birthdate).getFullYear();
    
    // Format 1: 10 characters - 9 digits followed by 'V' (YYXXXXXXXV)
    const format1Regex = /^([0-9]{2})[0-9]{7}V$/;
    const format1Match = trimmedNic.match(format1Regex);
    
    if (format1Match) {
      const nicYearLastTwo = parseInt(format1Match[1], 10);
      const birthYearLastTwo = birthYear % 100;
      
      if (nicYearLastTwo !== birthYearLastTwo) {
        return 'NIC number does not match the entered birth year. Please check and try again.';
      }
      return '';
    }
    
    // Format 2: 12 digits - only numbers (YYYYXXXXXXXX)
    const format2Regex = /^([0-9]{4})[0-9]{8}$/;
    const format2Match = trimmedNic.match(format2Regex);
    
    if (format2Match) {
      const nicYear = parseInt(format2Match[1], 10);
      
      if (nicYear !== birthYear) {
        return 'NIC number does not match the entered birth year. Please check and try again.';
      }
      return '';
    }
    
    // If NIC format is invalid, don't validate birthdate
    return '';
  };

  const handleNICChange = (field, value) => {
    // Update the form data
    setFormData({ ...formData, [field]: value });
    
    // Validate and update error state
    const error = validateNIC(value);
    setNicErrors({ ...nicErrors, [field]: error });
    
    // If this is the customer NIC, also validate birthdate
    if (field === 'nic') {
      const birthdateError = validateBirthdateWithNIC(value, formData.birthdate);
      setBirthdateError(birthdateError);
    }
  };

  const handleBirthdateChange = (value) => {
    // Update the form data
    setFormData({ ...formData, birthdate: value });
    
    // Validate birthdate against NIC
    const error = validateBirthdateWithNIC(formData.nic, value);
    setBirthdateError(error);
  };

  const handlePhoneChange = (field, value) => {
    // Update the form data
    setFormData({ ...formData, [field]: value });
    
    // Validate and update error state
    const error = validatePhoneNumber(value);
    setPhoneErrors({ ...phoneErrors, [field]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all NIC fields before submission
    const nicError = validateNIC(formData.nic);
    const spouseNicError = validateNIC(formData.spouseNic);
    const guarantorNicError = validateNIC(formData.guarantorNic);
    const guarantor2NicError = validateNIC(formData.guarantor2Nic);
    
    // Validate birthdate against NIC
    const birthdateErrorMsg = validateBirthdateWithNIC(formData.nic, formData.birthdate);
    
    // Validate all phone numbers
    const phoneError = validatePhoneNumber(formData.phoneNumber);
    const spousePhoneError = validatePhoneNumber(formData.spousePhoneNumber);
    const guarantorPhoneError = validatePhoneNumber(formData.guarantorPhoneNumber);
    const guarantor2PhoneError = validatePhoneNumber(formData.guarantor2PhoneNumber);
    
    setNicErrors({
      nic: nicError,
      spouseNic: spouseNicError,
      guarantorNic: guarantorNicError,
      guarantor2Nic: guarantor2NicError,
    });
    setBirthdateError(birthdateErrorMsg);
    setPhoneErrors({
      phoneNumber: phoneError,
      spousePhoneNumber: spousePhoneError,
      guarantorPhoneNumber: guarantorPhoneError,
      guarantor2PhoneNumber: guarantor2PhoneError,
    });
    
    // If any validation error exists, prevent submission
    // Note: spouseNicError and spousePhoneError are only set if fields are filled, so they're optional
    if (nicError || guarantorNicError || guarantor2NicError || birthdateErrorMsg || 
        phoneError || guarantorPhoneError || guarantor2PhoneError || 
        (formData.spouseNic && spouseNicError) || (formData.spousePhoneNumber && spousePhoneError)) {
      toast.warning('Please fix validation errors before submitting.');
      return;
    }
    
    try {
      // Convert empty strings to null for optional fields
      // Normalize NIC values to uppercase
      // Normalize phone numbers (remove dashes/spaces, keep only digits)
      const submitData = {
        ...formData,
        nic: formData.nic.trim().toUpperCase(),
        spouseNic: formData.spouseNic && formData.spouseNic.trim() ? formData.spouseNic.trim().toUpperCase() : null,
        guarantorNic: formData.guarantorNic ? formData.guarantorNic.trim().toUpperCase() : null,
        guarantor2Nic: formData.guarantor2Nic ? formData.guarantor2Nic.trim().toUpperCase() : null,
        phoneNumber: formData.phoneNumber.replace(/[^0-9]/g, ''), // Remove all non-digits
        spousePhoneNumber: formData.spousePhoneNumber && formData.spousePhoneNumber.trim() ? formData.spousePhoneNumber.replace(/[^0-9]/g, '') : null, // Remove all non-digits, or null if empty
        guarantorPhoneNumber: formData.guarantorPhoneNumber ? formData.guarantorPhoneNumber.replace(/[^0-9]/g, '') : null, // Remove all non-digits
        guarantor2PhoneNumber: formData.guarantor2PhoneNumber ? formData.guarantor2PhoneNumber.replace(/[^0-9]/g, '') : null, // Remove all non-digits
        spouseFullName: formData.spouseFullName && formData.spouseFullName.trim() ? formData.spouseFullName.trim() : null,
        numberOfDependents: formData.numberOfDependents ? parseInt(formData.numberOfDependents) : null,
        birthdate: formData.birthdate || null,
        assetsLands: formData.assetsLands || null,
        assetsVehicles: formData.assetsVehicles || null,
        assetsOther: formData.assetsOther || null,
        areaId: formData.areaId ? parseInt(formData.areaId) : null,
      };
      const response = await api.post('/officer/customers', submitData);
      toast.success('Customer registered successfully!');
      // Reset form and errors
      setNicErrors({
        nic: '',
        spouseNic: '',
        guarantorNic: '',
        guarantor2Nic: '',
      });
      setBirthdateError('');
      setPhoneErrors({
        phoneNumber: '',
        spousePhoneNumber: '',
        guarantorPhoneNumber: '',
        guarantor2PhoneNumber: '',
      });
      setFormData({
        fullName: '',
        address: '',
        businessAddress: '',
        gramaNiladhariDivision: '',
        nic: '',
        birthdate: '',
        occupation: '',
        registrationPeriod: '',
        phoneNumber: '',
        spouseFullName: '',
        spouseNic: '',
        spousePhoneNumber: '',
        numberOfDependents: '',
        assetsLands: '',
        assetsVehicles: '',
        assetsOther: '',
        areaId: '',
        bankAccountNumber: '',
        bankBranch: '',
        bankName: '',
        guarantorName: '',
        guarantorAddress: '',
        guarantorBusinessAddress: '',
        guarantorOccupation: '',
        guarantorNic: '',
        guarantorPhoneNumber: '',
        guarantor2Name: '',
        guarantor2Address: '',
        guarantor2BusinessAddress: '',
        guarantor2Occupation: '',
        guarantor2Nic: '',
        guarantor2PhoneNumber: '',
      });
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to register customer. Please check all fields and try again.';
      toast.error('Error registering customer: ' + errorMessage);
      
      // If it's a validation error, also set the specific field errors
      if (error.response?.data?.error) {
        const errorText = error.response.data.error;
        if (errorText.includes('NIC') && errorText.includes('birth year')) {
          setBirthdateError(errorText);
        } else if (errorText.includes('Phone number') && !errorText.includes('Spouse') && !errorText.includes('Guarantor')) {
          setPhoneErrors(prev => ({ ...prev, phoneNumber: errorText }));
        } else if (errorText.includes('Spouse phone number')) {
          setPhoneErrors(prev => ({ ...prev, spousePhoneNumber: errorText }));
        } else if (errorText.includes('Guarantor phone number')) {
          // Could be guarantor 1 or guarantor 2 - set both if needed
          if (errorText.includes('Guarantor 1')) {
            setPhoneErrors(prev => ({ ...prev, guarantorPhoneNumber: errorText }));
          } else if (errorText.includes('Guarantor 2')) {
            setPhoneErrors(prev => ({ ...prev, guarantor2PhoneNumber: errorText }));
          } else {
            // Default to guarantor 1 if unclear
            setPhoneErrors(prev => ({ ...prev, guarantorPhoneNumber: errorText }));
          }
        }
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Register Customer
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NIC"
                required
                value={formData.nic}
                onChange={(e) => handleNICChange('nic', e.target.value)}
                error={!!nicErrors.nic}
                helperText={nicErrors.nic || 'Format: 9 digits + V (e.g., 901234567V) or 12 digits (e.g., 199012345678)'}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                  maxLength: 12,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Address"
                value={formData.businessAddress}
                onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grama Niladhari Division"
                value={formData.gramaNiladhariDivision}
                onChange={(e) => setFormData({ ...formData, gramaNiladhariDivision: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Area"
                value={formData.areaId}
                onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
                required
                disabled={loadingAreas}
                helperText={loadingAreas ? 'Loading areas...' : 'Select the area for this customer'}
              >
                {loadingAreas ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : areas.length === 0 ? (
                  <MenuItem disabled>No areas available</MenuItem>
                ) : (
                  areas.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      {area.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Birthdate"
                type="date"
                value={formData.birthdate}
                onChange={(e) => handleBirthdateChange(e.target.value)}
                error={!!birthdateError}
                helperText={birthdateError || 'Birth year must match the NIC number'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Occupation / Business"
                value={formData.occupation}
                onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Period at the Above Address"
                value={formData.registrationPeriod}
                onChange={(e) => setFormData({ ...formData, registrationPeriod: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                required
                value={formData.phoneNumber}
                onChange={(e) => handlePhoneChange('phoneNumber', e.target.value)}
                error={!!phoneErrors.phoneNumber}
                helperText={phoneErrors.phoneNumber || 'Format: 0712345678 (mobile) or 0112345678 (landline)'}
                inputProps={{
                  maxLength: 12, // Allow for formatting with dashes/spaces
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name of Husband/Wife"
                value={formData.spouseFullName}
                onChange={(e) => setFormData({ ...formData, spouseFullName: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NIC of Husband/Wife"
                value={formData.spouseNic}
                onChange={(e) => handleNICChange('spouseNic', e.target.value)}
                error={!!nicErrors.spouseNic}
                helperText={nicErrors.spouseNic || 'Optional - Format: 9 digits + V (e.g., 901234567V) or 12 digits (e.g., 199012345678)'}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                  maxLength: 12,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number of Husband/Wife"
                value={formData.spousePhoneNumber}
                onChange={(e) => handlePhoneChange('spousePhoneNumber', e.target.value)}
                error={!!phoneErrors.spousePhoneNumber}
                helperText={phoneErrors.spousePhoneNumber || 'Optional - Format: 0712345678 (mobile) or 0112345678 (landline)'}
                inputProps={{
                  maxLength: 12, // Allow for formatting with dashes/spaces
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number of Dependents"
                type="number"
                value={formData.numberOfDependents}
                onChange={(e) => setFormData({ ...formData, numberOfDependents: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Assets
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Lands"
                value={formData.assetsLands}
                onChange={(e) => setFormData({ ...formData, assetsLands: e.target.value })}
                placeholder="e.g., Land details, location, etc."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Vehicles"
                value={formData.assetsVehicles}
                onChange={(e) => setFormData({ ...formData, assetsVehicles: e.target.value })}
                placeholder="e.g., Vehicle type, registration, etc."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Other"
                value={formData.assetsOther}
                onChange={(e) => setFormData({ ...formData, assetsOther: e.target.value })}
                placeholder="e.g., Other asset details"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            Bank Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bankAccountNumber}
                onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Branch"
                value={formData.bankBranch}
                onChange={(e) => setFormData({ ...formData, bankBranch: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            Guarantor 1 Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 1 Name"
                value={formData.guarantorName}
                onChange={(e) => setFormData({ ...formData, guarantorName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 1 NIC"
                value={formData.guarantorNic}
                onChange={(e) => handleNICChange('guarantorNic', e.target.value)}
                error={!!nicErrors.guarantorNic}
                helperText={nicErrors.guarantorNic || 'Format: 9 digits + V (e.g., 901234567V) or 12 digits (e.g., 199012345678)'}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                  maxLength: 12,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor 1 Address"
                value={formData.guarantorAddress}
                onChange={(e) => setFormData({ ...formData, guarantorAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor 1 Business Address"
                value={formData.guarantorBusinessAddress}
                onChange={(e) => setFormData({ ...formData, guarantorBusinessAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 1 Occupation"
                value={formData.guarantorOccupation}
                onChange={(e) => setFormData({ ...formData, guarantorOccupation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 1 Phone Number"
                value={formData.guarantorPhoneNumber}
                onChange={(e) => handlePhoneChange('guarantorPhoneNumber', e.target.value)}
                error={!!phoneErrors.guarantorPhoneNumber}
                helperText={phoneErrors.guarantorPhoneNumber || 'Format: 0712345678 (mobile) or 0112345678 (landline) - Optional'}
                inputProps={{
                  maxLength: 12, // Allow for formatting with dashes/spaces
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            Guarantor 2 Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 2 Name"
                value={formData.guarantor2Name}
                onChange={(e) => setFormData({ ...formData, guarantor2Name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 2 NIC"
                value={formData.guarantor2Nic}
                onChange={(e) => handleNICChange('guarantor2Nic', e.target.value)}
                error={!!nicErrors.guarantor2Nic}
                helperText={nicErrors.guarantor2Nic || 'Format: 9 digits + V (e.g., 901234567V) or 12 digits (e.g., 199012345678)'}
                inputProps={{
                  style: { textTransform: 'uppercase' },
                  maxLength: 12,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor 2 Address"
                value={formData.guarantor2Address}
                onChange={(e) => setFormData({ ...formData, guarantor2Address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guarantor 2 Business Address"
                value={formData.guarantor2BusinessAddress}
                onChange={(e) => setFormData({ ...formData, guarantor2BusinessAddress: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 2 Occupation"
                value={formData.guarantor2Occupation}
                onChange={(e) => setFormData({ ...formData, guarantor2Occupation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Guarantor 2 Phone Number"
                value={formData.guarantor2PhoneNumber}
                onChange={(e) => handlePhoneChange('guarantor2PhoneNumber', e.target.value)}
                error={!!phoneErrors.guarantor2PhoneNumber}
                helperText={phoneErrors.guarantor2PhoneNumber || 'Format: 0712345678 (mobile) or 0112345678 (landline) - Optional'}
                inputProps={{
                  maxLength: 12, // Allow for formatting with dashes/spaces
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" size="large">
              Register Customer
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CustomerRegistration;
